import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ListingPage.module.css';
import { GENERATED_SPEC_FIELDS, GENERATED_ENUM_CERT_FIELDS } from './specFieldsGenerated';

const humanise = v =>
  String(v)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

const fmtBool = v =>
  v ? <FormattedMessage id="ListingPage.fixedSpec.yes" defaultMessage="Yes" />
    : <FormattedMessage id="ListingPage.fixedSpec.no"  defaultMessage="No"  />;

// Ceramic / porcelain tile multi-enum value labels. The stored values are
// slugs (e.g. "water-resistant-bia-0-5"); these maps reproduce the exact
// filter-UI labels from configListing.materials.js so the listing page reads
// identically to the search filter. Used for the Performance spec row and the
// Certifications section — humanise() would mangle the standards notation
// ("Iso 10545 13 Pool Chemical Resistance"), so map explicitly.
const CERAMIC_TILE_PERF_LABELS = {
  'slip-resistant-r10':                   'Slip resistant (R10)',
  'slip-resistant-r11':                   'Slip resistant (R11+)',
  'barefoot-rated-din-51097-a-b-c':       'Barefoot rated (DIN 51097 A/B/C)',
  'water-resistant-bia-0-5':              'Water resistant (BIa ≤0.5%)',
  'pool-chemical-resistant-iso-10545-13': 'Pool / chemical resistant (ISO 10545-13)',
  'fire-rated':                           'Fire rated',
  'uv-stable':                            'UV stable',
  'anti-mould':                           'Anti-mould',
  'frost-resistant':                      'Frost resistant',
  'pei-iv-heavy-commercial':              'PEI IV+ (heavy commercial)',
  'dcof-0-42':                            'DCOF ≥0.42',
};
const CERAMIC_TILE_CERT_LABELS = {
  'sirim':                                 'SIRIM certified',
  'iso-10545':                             'ISO 10545 (tested)',
  'en-14411':                              'EN 14411',
  'bomba-approved':                        'BOMBA approved',
  'greenguard-gold':                       'GreenGuard Gold',
  'epd':                                   'EPD (Environmental Product Declaration)',
  'artisan-handmade-declaration':          'Artisan / handmade declaration',
  'iso-10545-13-pool-chemical-resistance': 'ISO 10545-13 (pool chemical resistance)',
};

// Wood multi-enum value labels. One combined map across finish / surface-texture /
// pattern / origin / performance — the slugs are unique across those five facets, so
// a single lookup serves all of them via the `wood-list` kind. Mirrors the
// configListing.materials.js option labels exactly (humanise() would mangle "UV-cured",
// "SE Asia", "Versailles / Chantilly", "Wear layer ≥4mm", etc.).
const WOOD_LABELS = {
  // finish
  'unfinished-raw': 'Unfinished / raw', 'oiled': 'Oiled', 'hardwax-oiled': 'Hardwax oiled',
  'lacquered': 'Lacquered', 'uv-cured': 'UV-cured', 'smoked-fumed': 'Smoked / fumed',
  'brushed': 'Brushed', 'washed-limed': 'Washed / limed', 'reactive-stain': 'Reactive stain',
  'patinated': 'Patinated',
  // surface texture
  'smooth': 'Smooth', 'brushed-wire-brushed': 'Brushed / wire-brushed', 'hand-scraped': 'Hand-scraped',
  'skip-planed': 'Skip-planed', 'rough-sawn': 'Rough-sawn', 'grooved-reeded': 'Grooved / reeded',
  'embossed-grain': 'Embossed grain',
  // pattern
  'straight-linear': 'Straight / linear', 'herringbone': 'Herringbone', 'chevron': 'Chevron',
  'versailles-chantilly': 'Versailles / Chantilly', 'brick-bond': 'Brick bond',
  'basketweave': 'Basketweave', 'random-natural': 'Random / natural', 'bookmatched': 'Bookmatched',
  'custom': 'Custom',
  // origin
  'malaysia-borneo': 'Malaysia / Borneo', 'se-asia': 'SE Asia', 'europe': 'Europe',
  'north-america': 'North America', 'africa': 'Africa', 'australia': 'Australia',
  'modified-thermally-treated': 'Modified / thermally treated', 'reclaimed-salvaged': 'Reclaimed / salvaged',
  // performance
  'termite-resistant-natural-species': 'Termite resistant (natural species)',
  'termite-treated-chemical': 'Termite treated (chemical)', 'anti-mould': 'Anti-mould',
  'uv-stable': 'UV stable', 'dimensionally-stable-high-humidity': 'Dimensionally stable (high humidity)',
  'structural-grade-mtib-class-1-2': 'Structural grade (MTIB Class 1/2)', 'wear-layer-4mm': 'Wear layer ≥4mm',
  'carb-phase-2-compliant': 'CARB Phase 2 compliant', 'low-voc': 'Low VOC', 'fire-treated-fr': 'Fire treated / FR',
};
const WOOD_CERT_LABELS = {
  'mtib': 'MTIB registered', 'mtcs': 'MTCS (PEFC) certified', 'fsc': 'FSC certified',
  'pefc': 'PEFC certified', 'rainforest-alliance': 'Rainforest Alliance', 'carb-phase-2': 'CARB Phase 2',
  'floorscore': 'FloorScore', 'epd': 'EPD (Environmental Product Declaration)',
};

// Laminate & Solid Surfaces (laminate_surfacing_*) multi-enum value labels. One
// combined map across finish / surface-texture / pattern / origin / composition /
// performance — slugs are unique across those facets (the two that repeat,
// 'embossed-registered' and 'metallic', carry the same label either way). Mirrors the
// configListing.materials.js option labels (humanise mangles "HPL — standard",
// "EN 438", "AC3+", "E1/E0", "Embossed / registered").
const LAM_SURF_LABELS = {
  // finish
  'matt': 'Matt', 'satin': 'Satin', 'gloss': 'Gloss', 'high-gloss': 'High gloss',
  'textured': 'Textured', 'embossed-registered': 'Embossed / registered',
  'suede-soft-touch': 'Suede / soft touch', 'metallic': 'Metallic',
  // surface texture
  'smooth': 'Smooth', 'lightly-textured': 'Lightly textured', 'wood-grain-feel': 'Wood grain feel',
  'stone-texture-feel': 'Stone texture feel', 'fabric-texture': 'Fabric texture', 'soft-touch': 'Soft touch',
  // pattern
  'wood-look': 'Wood look', 'stone-look': 'Stone look', 'concrete-look': 'Concrete look',
  'plain-solid': 'Plain / solid', 'abstract-fantasy': 'Abstract / fantasy', 'geometric': 'Geometric',
  'textile-woven': 'Textile / woven', 'custom': 'Custom',
  // origin
  'germany': 'Germany', 'switzerland': 'Switzerland', 'malaysia': 'Malaysia', 'china': 'China',
  'italy': 'Italy', 'usa': 'USA', 'belgium': 'Belgium', 'other': 'Other',
  // composition
  'hpl-standard': 'HPL — standard', 'hpl-postforming': 'HPL — postforming',
  'hpl-exterior-grade': 'HPL — exterior grade', 'compact-phenolic-laminate': 'Compact / phenolic laminate',
  'solid-surface-acrylic-corian-type': 'Solid surface — acrylic (Corian-type)',
  'solid-surface-modified-acrylic': 'Solid surface — modified acrylic',
  'laminate-flooring-hdf-core': 'Laminate flooring (HDF core)',
  'laminate-flooring-mdf-core': 'Laminate flooring (MDF core)',
  // performance
  'scratch-resistant': 'Scratch resistant', 'impact-resistant': 'Impact resistant',
  'heat-resistant': 'Heat resistant', 'moisture-resistant': 'Moisture resistant',
  'chemical-resistant': 'Chemical resistant', 'anti-bacterial-hygienic': 'Anti-bacterial / hygienic',
  'uv-stable': 'UV stable', 'fire-rated': 'Fire rated', 'wear-resistant-ac3': 'Wear resistant (AC3+)',
  'food-safe-nsf-51': 'Food-safe (NSF 51)', 'formaldehyde-free-e1-e0': 'Formaldehyde-free (E1/E0)',
};
const LAM_SURF_CERT_LABELS = {
  'en-438-hpl-standard': 'EN 438 (HPL standard)', 'carb-phase-2': 'CARB Phase 2', 'reach': 'REACH',
  'iso-9001': 'ISO 9001', 'greenguard-gold': 'GreenGuard Gold', 'fire-class-b-s2-d0': 'Fire class B-s2,d0',
  'epd': 'EPD (Environmental Product Declaration)', 'nsf-51-food-safe': 'NSF 51 (food-safe)',
  'fsc': 'FSC certified',
};

// Resilient flooring (resilient_flooring_*) multi-enum value labels — one combined map
// across finish / surface-texture / pattern / origin / composition / performance. Mirrors
// configListing.materials.js option labels (humanise mangles "LVT (luxury vinyl tile)",
// "Vinyl sheet — heterogeneous", "Slip resistant (R10+)", "Low VOC / FloorScore").
const RESIL_LABELS = {
  // finish
  'matte': 'Matte', 'satin': 'Satin', 'gloss': 'Gloss', 'high-gloss': 'High gloss',
  'embossed-textured': 'Embossed / textured', 'smooth': 'Smooth', 'anti-slip-treated': 'Anti-slip treated',
  // surface texture
  'embossed-wood-grain': 'Embossed / wood grain', 'embossed-stone-texture': 'Embossed / stone texture',
  'rough-anti-slip': 'Rough / anti-slip', 'natural': 'Natural',
  // pattern
  'wood-look': 'Wood look', 'stone-look': 'Stone look', 'concrete-look': 'Concrete look',
  'plain-solid': 'Plain / solid', 'geometric': 'Geometric', 'abstract': 'Abstract',
  // origin
  'malaysia': 'Malaysia', 'belgium': 'Belgium', 'germany': 'Germany', 'china': 'China',
  'usa': 'USA', 'sweden': 'Sweden', 'other': 'Other',
  // composition
  'lvt-luxury-vinyl-tile': 'LVT (luxury vinyl tile)',
  'spc-stone-plastic-composite': 'SPC (stone plastic composite)',
  'wpc-wood-plastic-composite': 'WPC (wood plastic composite)',
  'vinyl-sheet-homogeneous': 'Vinyl sheet — homogeneous',
  'vinyl-sheet-heterogeneous': 'Vinyl sheet — heterogeneous',
  'rubber-natural': 'Rubber — natural', 'rubber-synthetic-sbr': 'Rubber — synthetic SBR',
  'linoleum-natural-forbo': 'Linoleum (natural / Forbo)',
  // performance
  'slip-resistant-r10': 'Slip resistant (R10+)',
  'heavy-commercial-rated-class-34-43': 'Heavy commercial rated (class 34/43)',
  'waterproof': 'Waterproof', 'anti-static-esd': 'Anti-static (ESD)', 'acoustic-rated': 'Acoustic rated',
  'chemical-resistant': 'Chemical resistant', 'anti-microbial': 'Anti-microbial', 'fire-rated': 'Fire rated',
  'underfloor-heating-compatible': 'Underfloor heating compatible', 'low-voc-floorscore': 'Low VOC / FloorScore',
};
// Carpet & Rug (carpet_rug_*) multi-enum value labels. One combined map across
// finish / surface-texture / pattern / origin / composition / performance — slugs
// are unique across those facets. Mirrors the configListing.materials.js option
// labels (humanise mangles "Nylon PA6.6", "Loop / bouclé", "Class 33+", "Cfl").
const CARPET_RUG_LABELS = {
  // finish
  'cut-pile': 'Cut pile', 'loop-pile': 'Loop pile', 'cut-loop': 'Cut & loop', 'tufted': 'Tufted',
  'hand-knotted': 'Hand-knotted', 'hand-woven': 'Hand-woven', 'flatweave': 'Flatweave',
  'needle-felt': 'Needle felt', 'ribbed-corded': 'Ribbed / corded',
  // surface texture
  'smooth-low-pile': 'Smooth / low pile', 'medium-pile': 'Medium pile',
  'high-pile-plush': 'High pile / plush', 'loop-boucl': 'Loop / bouclé',
  'flatweave-sisal-look': 'Flatweave / sisal-look', 'ribbed': 'Ribbed',
  'hard-wearing-dense': 'Hard-wearing / dense',
  // pattern
  'plain-solid': 'Plain / solid', 'geometric': 'Geometric', 'floral': 'Floral',
  'abstract': 'Abstract', 'stripe': 'Stripe', 'tribal-ethnic': 'Tribal / ethnic',
  'tonal-heathered': 'Tonal / heathered', 'custom': 'Custom',
  // origin
  'malaysia': 'Malaysia', 'india': 'India', 'turkey': 'Turkey', 'belgium': 'Belgium',
  'new-zealand': 'New Zealand', 'china': 'China', 'nepal': 'Nepal', 'uk': 'UK',
  'usa': 'USA', 'other': 'Other',
  // composition
  'wool': 'Wool', 'nylon-pa6': 'Nylon PA6', 'nylon-pa6-6': 'Nylon PA6.6',
  'polyester-pet': 'Polyester (PET)', 'polypropylene-pp': 'Polypropylene (PP)',
  'recycled-nylon': 'Recycled nylon', 'blended': 'Blended',
  'natural-sisal-seagrass': 'Natural sisal / seagrass', 'coir': 'Coir',
  'rubber-backed-entrance-matting': 'Rubber-backed entrance matting',
  // performance
  'fire-rated-class-b-cfl': 'Fire rated (Class B / Cfl)', 'anti-static': 'Anti-static',
  'anti-microbial': 'Anti-microbial', 'heavy-commercial-class-33': 'Heavy commercial (Class 33+)',
  'acoustic-rated': 'Acoustic rated', 'moisture-resistant': 'Moisture resistant',
  'entrance-barrier-rated': 'Entrance barrier rated', 'stain-resistant': 'Stain resistant',
};
const CARPET_RUG_CERT_LABELS = {
  'oeko-tex-standard-100': 'Oeko-Tex Standard 100', 'cri-green-label-plus': 'CRI Green Label Plus',
  'reach': 'REACH', 'fr-tested-en-iso-9239': 'FR tested (EN ISO 9239)',
  'iso-10140-acoustic': 'ISO 10140 (acoustic)', 'epd': 'EPD (Environmental Product Declaration)',
  'cradle-to-cradle': 'Cradle to Cradle',
};

// Glass (glass_*) multi-enum value labels — one combined map across finish /
// surface-texture / pattern / origin / performance (slugs unique; the repeats
// 'textured-patterned' and 'tinted' carry the same label either way). Mirrors
// configListing.materials.js exactly (humanise mangles "Low-iron / ultra-clear",
// "Mirrored / back-silvered", "Thermal insulating (low U-value)").
const GLASS_LABELS = {
  // finish
  'clear': 'Clear', 'low-iron-ultra-clear': 'Low-iron / ultra-clear', 'frosted': 'Frosted',
  'sandblasted': 'Sandblasted', 'acid-etched': 'Acid-etched', 'tinted': 'Tinted',
  'mirrored-back-silvered': 'Mirrored / back-silvered', 'back-painted-lacquered': 'Back-painted / lacquered',
  'digital-printed': 'Digital printed', 'textured-patterned': 'Textured / patterned',
  'switchable-smart': 'Switchable / smart',
  // surface texture
  'smooth-flat': 'Smooth / flat', 'frosted-diffuse': 'Frosted / diffuse', 'ribbed': 'Ribbed',
  'structured': 'Structured',
  // pattern
  'clear-plain': 'Clear / plain', 'geometric': 'Geometric', 'floral-decorative': 'Floral / decorative',
  'wired-safety': 'Wired / safety', 'custom-digital-print': 'Custom digital print',
  'prismatic-refractive': 'Prismatic / refractive',
  // origin
  'germany': 'Germany', 'belgium': 'Belgium', 'france': 'France', 'china': 'China',
  'malaysia': 'Malaysia', 'italy': 'Italy', 'other': 'Other',
  // performance
  'fire-rated': 'Fire rated', 'acoustic-rated': 'Acoustic rated',
  'thermal-insulating-low-u-value': 'Thermal insulating (low U-value)', 'solar-control': 'Solar control',
  'anti-glare': 'Anti-glare', 'uv-blocking': 'UV blocking', 'safety-glazing': 'Safety glazing',
  'structural-load-bearing': 'Structural / load-bearing', 'privacy-switchable': 'Privacy / switchable',
  'impact-resistant': 'Impact resistant',
};
const GLASS_CERT_LABELS = {
  'sirim': 'SIRIM', 'ce-marked': 'CE marked', 'en-12150-tempered': 'EN 12150 (tempered)',
  'en-14449-laminated': 'EN 14449 (laminated)', 'bomba-fire-rated-glazing': 'BOMBA fire-rated glazing',
  'safety-glazing-standard': 'Safety glazing standard', 'en-1096-coated-glass': 'EN 1096 (coated glass)',
  'epd': 'EPD (Environmental Product Declaration)',
};

// Metal (metal_*) filter-facet value labels — finish / composition / surface-texture /
// performance / origin. The CableRod supplier rows (metal_material, metal_mesh_type…)
// render the raw display strings; these map the registered FILTER slugs so the vision/
// derived facets read with their canonical labels (humanise mangles "Mill finish (2B)",
// "Corrosion resistant (C3+)", "Mesh / open weave").
const METAL_LABELS = {
  // finish
  'mill-finish-2b': 'Mill finish (2B)', 'brushed-satin': 'Brushed / satin', 'hairline': 'Hairline',
  'non-directional-random-scratch': 'Non-directional / random scratch', 'mirror-polished': 'Mirror polished',
  'super-mirror': 'Super Mirror', 'bead-blasted': 'Bead blasted', 'sandblasted': 'Sandblasted',
  'etched': 'Etched', 'embossed-textured': 'Embossed / textured', 'pvd-coated': 'PVD coated',
  'anodised': 'Anodised', 'powder-coated': 'Powder coated', 'patinated-aged': 'Patinated / aged',
  'hot-dip-galvanised': 'Hot-dip galvanised',
  // composition
  'stainless-steel-304': 'Stainless steel 304', 'stainless-steel-316-316l-coastal': 'Stainless steel 316 / 316L (coastal)',
  'aluminium': 'Aluminium', 'brass': 'Brass', 'copper': 'Copper', 'bronze': 'Bronze',
  'corten-weathering-steel': 'Corten / weathering steel', 'mild-steel': 'Mild steel',
  'titanium-coated-stainless': 'Titanium-coated stainless',
  // surface texture
  'smooth-flat': 'Smooth / flat', 'brushed-directional': 'Brushed / directional', 'non-directional': 'Non-directional',
  'textured-embossed': 'Textured / embossed', 'perforated': 'Perforated', 'mesh-open-weave': 'Mesh / open weave',
  'etched-relief': 'Etched / relief',
  // performance
  'corrosion-resistant-c3': 'Corrosion resistant (C3+)', 'coastal-marine-rated-c4-c5': 'Coastal / marine rated (C4/C5)',
  'fire-rated-a1': 'Fire rated (A1)', 'bomba-approved': 'BOMBA approved', 'uv-stable-coating': 'UV stable coating',
  'salt-spray-tested-astm-b117': 'Salt spray tested (ASTM B117)', 'anti-fingerprint': 'Anti-fingerprint',
  'structural-rated': 'Structural rated',
  // origin
  'malaysia': 'Malaysia', 'germany': 'Germany', 'italy': 'Italy', 'france': 'France', 'china': 'China',
  'japan': 'Japan', 'india': 'India', 'other': 'Other',
};
const METAL_CERT_LABELS = {
  'sirim': 'SIRIM', 'bomba-approved': 'BOMBA approved', 'fire-rated-en-13501': 'Fire-rated (EN 13501)',
  'salt-spray-tested-astm-b117': 'Salt spray tested (ASTM B117)', 'qualicoat-powder-coat': 'QUALICOAT (powder coat)',
  'iso-9001': 'ISO 9001', 'epd': 'EPD (Environmental Product Declaration)',
};

// Concrete (concrete_*) filter-facet value labels — finish / surface-texture / pattern /
// origin / performance (humanise mangles "Polished — matte", "Slip resistant (R10+)",
// "Malaysia (in-situ)").
const CONCRETE_LABELS = {
  // finish
  'polished-matte': 'Polished — matte', 'polished-semi-gloss': 'Polished — semi-gloss',
  'polished-high-gloss': 'Polished — high gloss', 'brushed': 'Brushed', 'broom-finished': 'Broom-finished',
  'exposed-aggregate': 'Exposed aggregate', 'form-finished-smooth': 'Form-finished / smooth',
  'micro-smooth': 'Micro-smooth', 'coated-sealed': 'Coated / sealed',
  // surface texture
  'ultra-smooth': 'Ultra-smooth', 'smooth': 'Smooth', 'lightly-textured': 'Lightly textured',
  'broom': 'Broom', 'polished': 'Polished', 'raw-natural': 'Raw / natural',
  // pattern
  'plain-solid-colour': 'Plain / solid colour', 'pigmented-coloured': 'Pigmented / coloured',
  'aggregate-exposed': 'Aggregate exposed', 'seeded-pattern': 'Seeded pattern',
  'terrazzo-style-inset': 'Terrazzo-style inset', 'custom-engraved': 'Custom engraved',
  // origin
  'malaysia-in-situ': 'Malaysia (in-situ)', 'malaysia-prefab': 'Malaysia (prefab)',
  'europe-microcement-brand': 'Europe (microcement brand)', 'other': 'Other',
  // performance
  'slip-resistant-r10': 'Slip resistant (R10+)', 'chemical-resistant': 'Chemical resistant',
  'fire-rated': 'Fire rated', 'thermal-mass': 'Thermal mass', 'anti-mould': 'Anti-mould',
  'moisture-sealed': 'Moisture sealed', 'uv-stable': 'UV stable', 'heavy-traffic-rated': 'Heavy traffic rated',
  'impact-resistant': 'Impact resistant', 'voc-free': 'VOC-free',
  'underfloor-heating-compatible': 'Underfloor heating compatible',
};
const CONCRETE_CERT_LABELS = {
  'sirim': 'SIRIM', 'bomba-approved': 'BOMBA approved', 'slip-resistance-certified': 'Slip resistance certified',
  'gbi-eligible': 'GBI eligible', 'epd': 'EPD (Environmental Product Declaration)', 'voc-free': 'VOC-free',
};

// Textile (textile_*) filter-facet value labels — finish / surface-texture / pattern /
// origin / composition / performance (humanise mangles "Natural / raw", "Boucle / looped",
// "Polyester (PET)", "Martindale ≥30,000 rubs", "Songket (gold brocade)").
const TEXTILE_LABELS = {
  // finish
  'natural-raw': 'Natural / raw', 'pre-washed': 'Pre-washed', 'water-repellent-treated': 'Water repellent treated',
  'flame-retardant-coated': 'Flame retardant coated', 'coated-laminated': 'Coated / laminated', 'printed': 'Printed',
  'embroidered': 'Embroidered', 'hand-woven': 'Hand-woven', 'digitally-printed': 'Digitally printed',
  // surface texture
  'smooth-flat': 'Smooth / flat', 'woven-texture': 'Woven texture', 'boucle-looped': 'Boucle / looped',
  'velvet': 'Velvet', 'ribbed': 'Ribbed', 'embossed': 'Embossed', 'pleated': 'Pleated', 'open-weave': 'Open weave',
  // pattern
  'plain-solid': 'Plain / solid', 'woven-pattern': 'Woven pattern', 'printed-geometric': 'Printed — geometric',
  'printed-floral': 'Printed — floral', 'printed-abstract': 'Printed — abstract', 'stripe': 'Stripe',
  'batik-cultural': 'Batik / cultural', 'embroidered-motif': 'Embroidered motif', 'jacquard': 'Jacquard',
  'custom': 'Custom',
  // origin
  'malaysia': 'Malaysia', 'indonesia': 'Indonesia', 'india': 'India', 'belgium': 'Belgium', 'italy': 'Italy',
  'turkey': 'Turkey', 'china': 'China', 'japan': 'Japan', 'uk': 'UK', 'other': 'Other',
  // composition
  'cotton': 'Cotton', 'linen': 'Linen', 'silk': 'Silk', 'wool': 'Wool', 'polyester-pet': 'Polyester (PET)',
  'nylon-pa': 'Nylon (PA)', 'acrylic': 'Acrylic', 'bamboo-fibre': 'Bamboo fibre', 'recycled-fibre': 'Recycled fibre',
  'blended': 'Blended', 'batik-malaysian': 'Batik (Malaysian)', 'songket-gold-brocade': 'Songket (gold brocade)',
  'rattan-natural-weave': 'Rattan / natural weave',
  // performance
  'fr-flame-retardant': 'FR (flame retardant)', 'martindale-30-000-rubs': 'Martindale ≥30,000 rubs',
  'martindale-50-000-rubs': 'Martindale ≥50,000 rubs', 'waterproof': 'Waterproof',
  'uv-stable-lightfast': 'UV stable / lightfast', 'anti-microbial': 'Anti-microbial',
  'acoustic-absorbing': 'Acoustic absorbing', 'easy-clean-bleach-cleanable': 'Easy clean / bleach cleanable',
  'easy-clean-stain-resistant': 'Easy clean / stain-resistant',
};
const TEXTILE_CERT_LABELS = {
  'oeko-tex-standard-100': 'Oeko-Tex Standard 100', 'reach-compliant': 'REACH compliant',
  'fr-tested-bs-5852-nfpa-701': 'FR tested (BS 5852 / NFPA 701)', 'martindale-tested': 'Martindale tested',
  'grs-recycled-content': 'GRS (recycled content)', 'epd': 'EPD (Environmental Product Declaration)',
  'cradle-to-cradle': 'Cradle to Cradle',
};

// Specifications rows. Each entry: [publicData key, label, format] where
// format is 'enum' | 'bool' | 'num' | 'pct' | 'mpa' | 'mm' | 'text'.
//
// The dropdown unions ROCK-side (`stone_*`) and LAMINATE-side (`hpl_*` +
// some shared keys) — rows whose key isn't present on a given listing are
// filtered out, so a marble listing shows only stone rows and a WE
// laminate shows only laminate rows without any per-listing-type
// branching. Cheap, additive: extend the array for new listing types.
const SPEC_FIELDS = [
  // Wallcovering listings (Goodrich Global brands — Cole & Son, Sanderson,
  // Zoffany, Harlequin, Morris & Co, Scion, Omexco, Sangetsu, Elitis…).
  // Values carry their own unit (e.g. "0.52 m", "72 cm"), so type = text.
  ['wc_roll_width',                   'Roll width',                  'text'],
  ['wc_roll_length',                  'Roll length',                 'text'],
  ['wc_pattern_repeat',               'Pattern repeat',              'text'],
  ['wc_pattern_match',                'Pattern match',               'text'],
  ['wc_composition',                  'Composition',                 'text'],
  // Contract wallcovering (vinyl/textile — Vescom etc.). Values carry their
  // own unit / class string, so type = text.
  ['wc_total_weight',                 'Total weight',                'text'],
  ['wc_fire_classification',          'Fire classification',         'text'],
  ['wc_acoustic',                     'Acoustic absorption',         'text'],
  ['wc_light_fastness',               'Light fastness',              'text'],
  // Stone listings (Marble Emporium, Stone Empire, etc.)
  ['stone_material_type',             'Material type',               'enum'],
  ['stone_water_absorption_class',    'Water absorption class',      'enum'],
  ['stone_water_absorption_pct',      'Water absorption',            'pct'],
  ['stone_slip_resistance_class',     'Slip resistance',             'text'],
  ['stone_dcof',                      'DCOF',                        'num'],
  ['stone_barefoot_class',            'Barefoot slip class',         'text'],
  ['stone_mohs_hardness',             'Mohs hardness',               'num'],
  ['stone_flexural_strength_mpa',     'Flexural strength',           'mpa'],
  ['stone_fire_reaction_class',       'Fire reaction class',         'text'],
  ['stone_frost_resistance',          'Frost resistant',             'bool'],
  ['stone_thermal_cycling_resistant', 'Thermal cycling resistant',   'bool'],
  ['stone_chemical_resistance',       'Chemical resistant',          'bool'],
  ['stone_uv_stable',                 'UV stable',                   'bool'],
  ['stone_anti_mould',                'Anti-mould',                  'bool'],
  ['stone_coastal_rated',             'Coastal / salt-spray rated',  'bool'],
  ['stone_heat_resistant',            'Heat resistant',              'bool'],
  ['stone_food_safe',                 'Food safe',                   'bool'],
  ['stone_max_slab_size',             'Maximum slab size',           'text'],
  ['stone_min_thickness_mm',          'Minimum thickness',           'mm'],
  ['stone_bookmatching',              'Bookmatching available',      'bool'],
  ['stone_veining_movement',          'Veining movement',            'enum'],
  // Ceramic / porcelain tile listings (Feruni, MML, White Horse, Guocera,
  // Solare). Finish/pattern/surface/origin are the vision-tagged filter
  // facets — they double as spec rows so the Specifications section is
  // populated (a tile carries no stone_*/hpl_* keys). `ceramic_tile_performance`
  // is a labelled capability list (proper labels via CERAMIC_TILE_PERF_LABELS).
  ['ceramic_tile_pattern',            'Pattern',                     'enum-list'],
  ['ceramic_tile_finish',             'Finish',                      'enum-list'],
  ['ceramic_tile_surface_texture',    'Surface texture',             'enum-list'],
  ['ceramic_tile_origin',             'Origin',                      'enum-list'],
  ['ceramic_tile_performance',        'Performance',                 'ctile-perf'],
  // Laminate listings (WE Laminates / Lam Chuan).
  ['hpl_finish_name',                 'Surface finish',              'text'],
  ['hpl_thickness_mm',                'Thickness',                   'mm'],
  ['hpl_dimension',                   'Sheet dimension',             'text'],
  ['hpl_compact',                     'Compact (HPL)',               'bool'],
  ['hpl_antibacterial',               'Antibacterial',               'bool'],
  ['hpl_edgeband_available',          'Matching edgeband available', 'bool'],
  // Laminate & Solid Surfaces filter facets (laminate_surfacing_*) — Nilam
  // melamine/HPL decor panels (vision finish/surface/pattern; origin + composition
  // applied). `lam-surf-list` maps slugs to the exact filter-UI labels via
  // LAM_SURF_LABELS (humanise mangles "HPL — standard", "AC3+", "E1/E0").
  ['laminate_surfacing_finish',         'Finish',          'lam-surf-list'],
  ['laminate_surfacing_surface_texture','Surface texture', 'lam-surf-list'],
  ['laminate_surfacing_pattern',        'Pattern',         'lam-surf-list'],
  ['laminate_surfacing_origin',         'Origin',          'lam-surf-list'],
  ['laminate_surfacing_composition',    'Composition',     'lam-surf-list'],
  ['laminate_surfacing_performance',    'Performance',     'lam-surf-list'],

  // ── Flooring measured specs (resilient + laminate + composite decking). New
  //    flooring_* / laminate_ac_rating / resilient_wear_layer_mm keys carried
  //    only by flooring listings — additive, invisible to every other family.
  //    Laminate facets already render above (lam-surf-list); resilient facets get
  //    their own resil-list rows here so both families show the same completeness.
  ['flooring_thickness_mm',             'Thickness',       'mm'],
  ['flooring_plank_size',               'Plank / sheet size', 'text'],
  ['laminate_ac_rating',                'Abrasion (AC)',   'text'],
  ['resilient_wear_layer_mm',           'Wear layer',      'text'],
  ['flooring_core',                     'Core',            'text'],
  ['flooring_locking',                  'Installation',    'text'],
  ['flooring_tone',                     'Tone',            'text'],
  ['resilient_flooring_pattern',        'Pattern',         'resil-list'],
  ['resilient_flooring_finish',         'Finish',          'resil-list'],
  ['resilient_flooring_surface_texture','Surface texture', 'resil-list'],
  ['resilient_flooring_composition',    'Composition',     'resil-list'],
  ['resilient_flooring_performance',    'Performance',     'resil-list'],
  ['resilient_flooring_origin',         'Origin',          'resil-list'],
  // Carpet & Rug filter facets (carpet_rug_*) — Paragon broadloom/tile. Same shape
  // as the laminate block; `carpet-rug-list` maps slugs to the filter-UI labels via
  // CARPET_RUG_LABELS (humanise mangles "Nylon PA6.6", "Loop / bouclé", "Class 33+").
  ['carpet_rug_finish',           'Pile construction', 'carpet-rug-list'],
  ['carpet_rug_surface_texture',  'Surface texture',   'carpet-rug-list'],
  ['carpet_rug_pattern',          'Pattern',           'carpet-rug-list'],
  ['carpet_rug_composition',      'Composition',       'carpet-rug-list'],
  ['carpet_rug_performance',      'Performance',       'carpet-rug-list'],
  ['carpet_rug_origin',           'Origin',            'carpet-rug-list'],
  // Carpet & LVT listings (Victorex + Interface). The supplier xlsx only
  // carries display-side fields — `fibre` / `pile_construction` from the
  // schema are filter-only and not surfaced here. Designers care about
  // colourway name+code (matches the supplier's specifier samples kit)
  // and the physical tile size; everything else is commerce.
  ['carpet_type',                     'Product type',                'enum'],
  ['carpet_colour_name',              'Colour name',                 'text'],
  ['carpet_colour_code',              'Colour code',                 'text'],
  ['carpet_tile_size_mm',             'Tile size',                   'text'],
  ['carpet_backing',                  'Backing',                     'text'],
  // Metal mesh / drapery listings (CableRod). `metal_*` columns come
  // straight from the supplier xlsx — preferred over the schema-canonical
  // `mesh_type` / `base_metal` enums for display (raw is more specific:
  // "AISI 304 Stainless Steel" beats "stainless-steel"). The
  // schema-canonical fields stay on publicData for filterability.
  ['metal_material',                  'Material',                    'text'],
  ['metal_mesh_type',                 'Mesh type',                   'enum'],
  ['metal_finish',                    'Finish',                      'metal-list'],
  ['metal_colour',                    'Colour / treatment',          'text'],
  ['metal_ring_outer_mm',             'Ring outer diameter',         'mm'],
  ['metal_ring_inner_mm',             'Ring inner diameter',         'mm'],
  ['metal_wire_diameter_mm',          'Wire diameter',               'mm'],
  ['metal_aperture_mm',               'Aperture',                    'mm'],
  ['metal_thickness_mm',              'Mesh thickness',              'mm'],
  ['metal_weight_kg_m2',              'Weight',                      'kg-m2'],
  ['metal_remarks',                   'Supplier remarks',            'text'],
  // CableRod registered filter facets, derived from the supplier material/finish.
  // Composition + surface-texture already read via metal_material / metal_mesh_type
  // above, so only performance + origin add new rows; metal_certifications renders
  // in the Certifications section (ENUM_CERT_FIELDS). `metal-list` → METAL_LABELS.
  ['metal_performance',               'Performance',                 'metal-list'],
  ['metal_origin',                    'Origin',                      'metal-list'],
  // Decorative-metal enum facets (CTC Titanium PVD sheets etc.) — brands
  // without the raw-text metal_material/mesh rows above carry these enums
  // instead; distinct labels so a brand with both never double-renders
  // "Material". `metal-list` → METAL_LABELS slugs → labels.
  ['metal_composition',               'Composition',                 'metal-list'],
  ['metal_surface_texture',           'Surface texture',             'metal-list'],
  // ── Electrical accessories (switches & sockets) — JUNG, Schneider, etc. ──
  // Hand-maintained text rows; isPresent()-gated so only electrical listings
  // carry+show them. electrical-accessories ships no generated spec rows, so
  // without these the Specifications section would render empty for switches.
  ['elec_dimensions',                 'Dimensions',                  'text'],
  ['elec_material',                   'Material',                    'text'],
  ['elec_rating',                     'Electrical rating',           'text'],
  ['elec_protection',                 'Protection (IP)',             'text'],
  ['elec_mounting',                   'Mounting',                    'text'],
  ['elec_standard',                   'Standards',                   'text'],
  ['elec_origin',                     'Origin',                      'text'],
  ['elec_since',                      'In production since',         'text'],
  // ── Door & cabinet hardware (Buster + Punch, enriched 2026-07-22) ──
  // Values come from the brand's own PIM spec rows on uk.busterandpunch.com
  // product pages (onetimepim inline blob), so each carries its own unit
  // string (e.g. '125mm / 4.92"'). Dimensions / Material / Origin reuse the
  // elec_* rows above, which these listings already carry. Additive: hw_*
  // keys exist only on door-cabinet-hardware listings.
  ['hw_centres',                      'Fixing centres',              'text'],
  ['hw_diameter',                     'Bar / knob diameter',         'text'],
  ['hw_projection',                   'Projection',                  'text'],
  ['hw_rose_diameter',                'Rose diameter',               'text'],
  ['hw_spindle',                      'Spindle',                     'text'],
  ['hw_latch_compatibility',          'Latch compatibility',         'text'],
  ['hw_max_door_thickness',           'Max door / drawer thickness', 'text'],
  ['hw_plate_material',               'Plate material',              'text'],
  ['hw_coating',                      'Coating',                     'text'],
  ['hw_fixing',                       'Fixing',                      'text'],
  ['hw_screw',                        'Screws',                      'text'],
  ['hw_fire_rating',                  'Fire approval',               'text'],
  ['hw_ce_grade',                     'CE grade',                    'text'],
  ['hw_cord_length',                  'Cord length',                 'text'],
  ['hw_included',                     'In the box',                  'text'],
  ['hw_care',                         'Care',                        'text'],
  // ── Tapware & fittings (GROHE enrichment 2026-07-23) ──
  // Values come from the brand's own PDP technical-spec tables (grohe.us
  // PIM), each carrying its own unit string (e.g. '1.75 gal (US)/min',
  // '12.70 mm', '8-5/16 in'). Generic tap_* keys — any tapware brand can
  // carry them. Additive: rows appear only on listings that set them.
  ['tap_dimensions',                  'Dimensions',                  'text'],
  ['tap_height',                      'Height',                      'text'],
  ['tap_width',                       'Width',                       'text'],
  ['tap_depth',                       'Depth',                       'text'],
  ['tap_length',                      'Length',                      'text'],
  ['tap_weight',                      'Weight',                      'text'],
  ['tap_flow_rate',                   'Flow rate',                   'text'],
  ['tap_connection_size',             'Connection size',             'text'],
  ['tap_hose_length',                 'Hose length',                 'text'],
  ['tap_spout_reach',                 'Spout reach',                 'text'],
  ['tap_swivel',                      'Swivel',                      'text'],
  ['tap_material',                    'Material',                    'text'],
  ['tap_valve_material',              'Valve material',              'text'],
  ['tap_valve_needed',                'Valve required for install',  'text'],
  ['tap_handle_type',                 'Handle type',                 'text'],
  ['tap_handle_material',             'Handle material',             'text'],
  ['tap_handle_height',               'Handle height',               'text'],
  ['tap_holes_required',              'Holes required',              'text'],
  ['tap_max_deck_thickness',          'Max deck thickness',          'text'],
  ['tap_max_deck_thickness_esc',      'Max deck thickness (with escutcheon)', 'text'],
  ['tap_side_spray',                  'Side spray included',         'text'],
  ['tap_hose_included',               'Hose included',               'text'],
  ['tap_battery',                     'Battery',                     'text'],
  ['tap_batteries_required',          'Batteries required',          'text'],
  ['tap_batteries_included',          'Batteries included',          'text'],
  ['tap_mount_type',                  'Mount type',                  'text'],
  ['tap_hardware_included',           'Mounting hardware included',  'text'],
  ['tap_hook_type',                   'Hook type',                   'text'],
  ['tap_hanger_type',                 'Hanger type',                 'text'],
  ['tap_features',                    'Features',                    'text'],
  ['tap_finishes',                    'Available finishes',          'text'],
  ['tap_article_numbers',             'Article numbers (by finish)', 'text'],
  ['tap_operating_pressure',          'Operating pressure',          'text'],
  // Self-closing / timed-flow / sensor tapware (Presto enrichment 2026-07-24).
  // Values come from Presto's own technical datasheets (prestodatashare.com
  // fiches, GB) — each carries its own unit string (e.g. '1 to 5 bar',
  // '15 seconds ( ± 5 sec.)', '75°C'). Generic tap_* keys — any commercial
  // tapware brand can carry them. Additive: rows appear only on listings that
  // set them, so existing GROHE tapware is unaffected.
  ['tap_working_pressure',            'Working pressure',            'text'],
  ['tap_flow_time',                   'Flow time',                   'text'],
  ['tap_operation',                   'Operation',                   'text'],
  ['tap_power_supply',                'Power supply',                'text'],
  ['tap_thermal_resistance',          'Thermal resistance',          'text'],
  ['tap_security',                    'Security',                    'text'],
  ['tap_installation',                'Installation',                'text'],
  ['tap_standards',                   'Standards & approvals',       'text'],
  // Sanitaryware rows for GROHE toilet tanks/bowls (live under
  // tapware-fittings) — san_* generic, reusable by sanitaryware brands.
  ['san_toilet_type',                 'Toilet type',                 'text'],
  ['san_bowl_shape',                  'Bowl shape',                  'text'],
  ['san_flush_technology',            'Flush technology',            'text'],
  ['san_flushing_type',               'Flushing type',               'text'],
  ['san_gpf_max',                     'Flush volume (max)',          'text'],
  ['san_gpf_min',                     'Flush volume (min)',          'text'],
  ['san_insulated_tank',              'Insulated tank',              'text'],
  ['san_seat_front_type',             'Seat front type',             'text'],
  ['san_tank_included',               'Tank included',               'text'],
  ['san_seat_included',               'Seat included',               'text'],
  // Kitchen sinks (Reginox, live under appliances-kitchen) — sink_* generic,
  // reusable by any sink brand. Values come from the brand's PDP spec table
  // (reginox.co.uk), each carrying its own unit; text rows, additive, gated to
  // listings that set them. Dimension strings already include 'mm'.
  ['sink_material',                   'Material',                    'text'],
  ['sink_mount_type',                 'Mounting',                    'text'],
  ['sink_bowls',                      'Number of bowls',             'text'],
  ['sink_reversible',                 'Reversible',                  'text'],
  ['sink_shape',                      'Bowl shape',                  'text'],
  ['sink_total_size',                 'Overall size',                'text'],
  ['sink_bowl_size',                  'Main bowl size',              'text'],
  ['sink_cabinet_size',               'Min. cabinet size',           'text'],
  ['sink_thickness',                  'Material thickness',          'text'],
  ['sink_depth',                      'Bowl depth',                  'text'],
  ['sink_inner_radius',               'Inner radius',                'text'],
  ['sink_outer_radius',               'Outer radius',                'text'],
  ['sink_diameter',                   'Diameter',                    'text'],
  ['sink_strainer',                   'Strainer / waste code',       'text'],
  ['sink_colour',                     'Colour',                      'text'],
  ['sink_dimensions',                 'Dimensions (H×W×L)',          'text'],
  // Specialty paint listings (SUZUKA — STRATO Cement Texture, Velours,
  // etc.). `paint_finish_type` carries supplier-specific texture
  // vocabulary not present in the canonical schema enum
  // ("concrete-cement-effect", "decorative-plaster", "fabric-texture")
  // — render the raw value rather than forcing a wrong map. Panel
  // dimensions are sparse — only on ARCHISTRA / KASTPANEL rows once
  // they leave HOLD status.
  ['paint_finish_type',               'Finish family',               'enum'],
  ['paint_surface_texture',           'Surface texture',             'enum'],
  ['panel_size',                      'Panel size',                  'text'],
  ['panel_thickness',                 'Panel thickness',             'text'],
  ['panel_weight',                    'Panel weight',                'text'],
  // Packing & coverage — scraped from supplier category page by
  // enrich_suzuka_collection.py. Free-text because the supplier's
  // format varies per collection (e.g. "1L Primer + 1L Texture Coat,
  // 6–7 sqm/set").
  ['packing_coverage',                'Packing & coverage',          'text'],
  // Solid timber listings (Hengwood, Asia Timber House). `wood_species`
  // is the raw supplier display value; the schema-canonical `species`
  // enum slug is filter-only and not shown. `wood_janka_hardness_n` is
  // a wood-only field (Janka hardness in newtons) — useful spec for
  // designers picking species for high-traffic flooring or decking.
  // `wood_applications` (plural — note the 's') is the consolidated
  // multi-tag list written by consolidate_<brand>.py. Pre-consolidation
  // uploaders write singular `wood_application` instead, so both keys
  // are rendered; whichever is present wins.
  ['wood_species',                    'Wood species',                'text'],
  ['wood_applications',               'Intended applications',       'enum-list'],
  ['wood_application',                'Intended application',        'enum'],
  ['wood_janka_hardness_n',           'Janka hardness',              'janka'],
  // Wood filter facets (vision-tagged finish/surface/pattern + species-derived
  // origin; performance from per-brand recon). `wood-list` maps the slugs to the
  // exact filter-UI labels via WOOD_LABELS (humanise mangles UV-cured / SE Asia / …).
  ['wood_finish',                     'Finish',                      'wood-list'],
  ['wood_surface_texture',            'Surface texture',             'wood-list'],
  ['wood_pattern',                    'Pattern',                     'wood-list'],
  ['wood_origin',                     'Origin',                      'wood-list'],
  ['wood_performance',                'Performance',                 'wood-list'],
  // Glass listings (Matterax Curated 2026-05-29 — sourced from
  // architecturalglass.com Shopify catalogue). Values carry their own unit
  // (e.g. "1/4\"", "84\" x 144\"", "0.23 lbs"), so type = text. Safety
  // options are PROCESSING options ("available as Tempered/Laminated/IGU"),
  // never base-product properties — phrasing handled in build_payload.
  ['gl_thickness',                    'Thickness',                   'text'],
  ['gl_thicknesses_available',        'Available thicknesses',       'enum-list'],
  ['gl_sheet_size',                   'Standard sheet size',         'text'],
  ['gl_weight',                       'Weight',                      'text'],
  ['gl_safety_options',               'Available processing',        'text'],
  // Glass registered filter facets (vision-tagged finish/surface/pattern; performance +
  // origin from recon). The gl_* rows above are the supplier display fields; these are the
  // discovery facets, so a decorative-glass listing shows both. `glass-list` → GLASS_LABELS.
  ['glass_finish',                    'Finish',                      'glass-list'],
  ['glass_surface_texture',           'Surface texture',             'glass-list'],
  ['glass_pattern',                   'Pattern',                     'glass-list'],
  ['glass_origin',                    'Origin',                      'glass-list'],
  ['glass_performance',               'Performance',                 'glass-list'],
  // Concrete & cementitious filter facets (Granopave etc.) — vision finish/surface/pattern;
  // performance + origin from recon. `concrete-list` → CONCRETE_LABELS.
  ['concrete_finish',                 'Finish',                      'concrete-list'],
  ['concrete_surface_texture',        'Surface texture',             'concrete-list'],
  ['concrete_pattern',                'Pattern',                     'concrete-list'],
  ['concrete_origin',                 'Origin',                      'concrete-list'],
  ['concrete_performance',            'Performance',                 'concrete-list'],
  // Textile / fabric filter facets (Jotex, Fibreguard, Fabric Library…) — vision finish/
  // surface/pattern; composition + performance from recon. `textile-list` → TEXTILE_LABELS.
  ['textile_finish',                  'Finish',                      'textile-list'],
  ['textile_surface_texture',         'Surface texture',             'textile-list'],
  ['textile_pattern',                 'Pattern',                     'textile-list'],
  ['textile_composition',             'Composition',                 'textile-list'],
  ['textile_origin',                  'Origin',                      'textile-list'],
  ['textile_performance',             'Performance',                 'textile-list'],
  // Roll width + areal weight (byKaeen, 2026-07-30). Deliberately NOT adding
  // fibre_content / fr_rating / contract_suitability alongside these: the
  // supplier data duplicates keys that already render — fibre_content repeats
  // textile_composition on all 229 rows, and fr_rating "Yes" is exactly the
  // 131 rows whose textile_performance is already fr-flame-retardant. Two
  // "Composition" rows disagreeing on wording would read as a data bug.
  ['width_cm',                        'Roll width',                  'cm'],
  ['weight_g_m',                      'Weight',                      'gsm'],
  // Commercial / use-context (cross-brand). Single "Exterior rated"
  // boolean (the old interior+exterior pair collapsed 2026-05-28 — interior
  // use is the default for all materials, so only the exterior exception
  // carries signal). Key stays `exterior`; only the label changed.
  // Warranty years is a wood-side commercial detail but the field shape
  // works for any category.
  ['exterior',                        'Exterior rated',              'bool'],
  ['warranty_years',                  'Warranty',                    'years'],
  // Shared commercial / availability (laminate + stone, where present).
  ['standard_sizes',                  'Standard sizes',              'text'],
  ['custom_sizes_available',          'Custom sizes available',      'bool'],
  ['moq',                             'Minimum order quantity',      'text'],
  ['lead_time_days_numeric',          'Lead time (days)',            'num'],
  ['installation_available',          'Installation service',        'bool'],
  // Generic product specs for the 2026-07-22 FF&E + architectural families —
  // Roca sanitaryware/tapware, Bristol/Commune furniture, Flexiwall acoustic
  // systems, KEDING veneer/HPL, Pepperwall. Values carry their own unit, so
  // type = text; isPresent() shows each row only on listings that set it.
  ['spec_dimensions',                 'Dimensions',                  'text'],
  ['spec_material',                   'Material',                    'text'],
  ['spec_finish',                     'Finish',                      'text'],
  ['spec_wood_species',               'Wood species',                'text'],
  ['spec_acoustic_rating',            'Acoustic rating',             'text'],
  ['spec_mounting',                   'Mounting',                    'text'],
  ['spec_origin',                     'Origin',                      'text'],
  // Washroom-accessory commercial specs (Bobrick 2026-07-24). Capacity values
  // carry their own unit ("34-fl oz (1.0-L)", "12-gal (45.5-L)", "600 C-fold"),
  // so type = text. Additive — invisible to listings without the key.
  ['spec_capacity',                   'Capacity',                    'text'],
];

// Certifications = third-party / regulatory standards. A cert means an
// external body (USGBC, SIRIM, ISO, UL, etc.) has issued or recognised
// the product against a published criterion. Distinct from FEATURE_FIELDS
// below, which are supplier-side property claims.
const CERT_FIELDS = [
  // Stone certifications
  ['stone_cert_sirim',         'SIRIM certified'],
  ['stone_cert_ce',            'CE marked'],
  ['stone_cert_iso9001',       'ISO 9001'],
  ['stone_cert_fire_en13501',  'Fire-rated EN 13501'],
  ['stone_cert_leed',          'LEED / GBI eligible'],
  ['stone_cert_nsf51',         'NSF 51 (food safe)'],
  ['stone_cert_greenguard',    'GREENGUARD (Low-emitting / Indoor Air Quality)'],
  // Laminate certifications (set as brand-level on every WE listing).
  ['lam_cert_greenguard',      'GREENGUARD Indoor Air Quality'],
  ['lam_cert_fire',            'Class 0 fire rating (BS 476)'],
  // Solid timber certifications (Hengwood + others).
  ['wood_cert_pefc',           'PEFC certified'],
  ['wood_cert_nwfa',           'NWFA member'],
  // Carpet certifications (Victorex + Interface — per-row from xlsx).
  ['carpet_cert_greenguard',   'GREENGUARD Gold (Indoor Air Quality)'],
  ['carpet_cert_iso',          'ISO 14001 / 9001'],
  // Specialty paint certifications (SUZUKA — per-row from xlsx, all
  // SUZUKA listings carry these uniformly). A future
  // patch_suzukacoat_brand_certs.py could promote to brand-level.
  ['cert_singapore_green_label', 'Singapore Green Label'],
  ['cert_sirim_qas',             'SIRIM QAS certified'],
  ['cert_iso9001',               'ISO 9001'],
  // SUZUKA management-system certs (catalogue footers, e.g. MONOKRETE).
  ['cert_iso14001',              'ISO 14001 (Environmental)'],
  ['cert_iso45001',              'ISO 45001 (Occupational H&S)'],
  // Collection-level certs scraped from suzuka.com.my category pages
  // by enrich_suzuka_collection.py (May 2026). LEED & CA Prop 65 are
  // legitimate third-party standards; Green Product / Low VOC etc.
  // are supplier-side claims and live in FEATURE_FIELDS instead.
  ['cert_leed',                  'LEED — US Green Building Council'],
  ['cert_california_prop_65',    'California Proposition 65 compliant'],
  // ARCHISTRA / panel collections (May 2026, from catalogue). Fire
  // Rating B1 cites DIN 4102 — a third-party fire classification, so it
  // belongs with certs, not features.
  ['cert_fire_b1_din',           'Fire rating B1 (DIN 4102)'],
  // Contract wallcovering certs (Vescom etc., 2026-05-29). Set per product
  // family from the supplier datasheet — IMO only on marine-grade ranges,
  // CE Vinyl vs CE Textile per substrate, etc. VERIFY before setting true.
  ['wc_cert_ce',                 'CE marked (EN 15102)'],
  ['wc_cert_sfec_aplus',         'SFEC Emission A+ (low-VOC indoor air)'],
  ['wc_cert_vinylplus',          'VinylPlus (sustainable PVC)'],
  ['wc_cert_imo',                'IMO 2010 FTP Code (marine fire)'],
  ['wc_cert_nfpa286',            'NFPA 286 (room-corner fire test)'],
  // FSC (Omexco + Sanderson Design Group, brand-wide verified 2026-05-29);
  // F-four-star formaldehyde (Sangetsu, JIS/ministerial, per-SKU).
  ['wc_cert_fsc',                'FSC-certified (responsible forestry)'],
  ['wc_cert_f4star',             'F☆☆☆☆ formaldehyde (low-emission, JIS/JAS)'],
  // Contract furniture (Bristol — brand-wide SCS certificate, ANSI/BIFMA + CA 01350).
  ['cert_scs_indoor_advantage',  'SCS Indoor Advantage Gold (low-VOC IAQ)'],
  // Washroom-accessory accessibility compliance (Bobrick 2026-07-24 — values
  // come from the manufacturer's own per-product accessibility facets).
  ['cert_ada_compliant',         'ADA compliant (Americans with Disabilities Act)'],
  ['cert_dda_compliant',         'DDA compliant (UK Disability Discrimination Act)'],
  ['cert_docm_compliant',        'Doc M compliant (UK Building Regulations)'],
];

// Features = supplier-marketed property claims. Not third-party
// certified. Rendered in a separate "Features" dropdown so designers
// can tell at a glance which checkmarks are externally verified vs.
// brand-attested. See SectionStoneSpecs.FeaturesBody.
const FEATURE_FIELDS = [
  ['feat_green_product',         'Green Product (Eco-friendly)'],
  ['feat_low_voc_odor',          'Low VOC & Odor'],
  ['feat_formaldehyde_lead_free','Formaldehyde & Lead Free'],
  ['feat_water_based_eco',       'Water-based, Eco-friendly'],
  ['feat_applicable_ceiling',    'Applicable on Ceiling'],
  ['feat_easy_maintenance',      'Easy Maintenance'],
  ['feat_easy_application',      'Easy Application / DIY-friendly'],
  ['feat_metallic_finish',       'Metallic finishing'],
  ['feat_anti_mould',            'Anti-mould'],
  ['feat_antibacterial',         'Anti-bacterial'],
  ['feat_antifungal',            'Anti-fungal'],
  ['feat_anti_yellowing',        'Anti-yellowing'],
  ['feat_cool_roof',             'Cool Roof / Heat reflective'],
  ['feat_water_resistant',       'Water resistant'],
  // ARCHISTRA / panel collections (May 2026, from catalogue).
  ['feat_ultra_lightweight',     'Ultra lightweight'],
  ['feat_paintable',             'Paintable'],
  ['feat_anti_termite',          'Anti-termite'],
  ['feat_green_coating',         'Finished with green coating'],
  ['feat_pairs_suzuka_coatings', 'Pairs with other Suzuka coatings'],
  ['feat_made_in_malaysia',      'Made in Malaysia'],
  // MONOKRETE cement textured paint (May 2026, from catalogue).
  ['feat_anti_chalking',         'Anti-chalking & efflorescence'],
  ['feat_easy_touchup',          'Easy to touch up'],
  // STRATO interior textured paint (May 2026, from catalogue).
  ['feat_crack_resistant',       'Crack resistant'],
  // Wallcovering features — verified from cole-and-son.com (May 2026):
  // non-woven backing is standard; all hung paste-the-wall; non-woven
  // ranges are sponge-wipeable (silk/grasscloth/flock are NOT, so
  // feat_wc_spongeable is set per-listing, excluding those substrates).
  ['feat_wc_paste_the_wall',     'Paste-the-wall application'],
  ['feat_wc_non_woven',          'Non-woven substrate'],
  ['feat_wc_spongeable',         'Spongeable / wipeable'],
  ['feat_wc_scrubbable',         'Scrubbable / stain-resistant'],
  ['feat_wc_pvc_free',           'PVC-free'],
  // Electrical accessories (switches / sockets) — supplier-attested claims.
  ['feat_scratch_resistant',     'Scratch-resistant surface'],
  ['feat_colourfast',            'Colour-fast for life'],
  ['feat_halogen_free',          'Halogen-free'],
  ['feat_bs_compatible',         'British Standard versions available'],
  ['feat_knx_compatible',        'KNX / smart-home compatible'],
  ['feat_made_in_germany',       'Made in Germany'],
];

// Multi-enum certification fields (array of slugs) rendered in the
// Certifications section alongside the boolean *_cert_* flags above. Each
// entry: [publicData key, slug→label map]. A listing carries either the
// boolean flags (stone/laminate/wood…) or one of these arrays, never both,
// so the two render passes don't collide.
const ENUM_CERT_FIELDS = [
  ['ceramic_tile_certifications', CERAMIC_TILE_CERT_LABELS],
  ['wood_certifications', WOOD_CERT_LABELS],
  ['laminate_surfacing_certifications', LAM_SURF_CERT_LABELS],
  ['carpet_rug_certifications', CARPET_RUG_CERT_LABELS],
  ['glass_certifications', GLASS_CERT_LABELS],
  ['metal_certifications', METAL_CERT_LABELS],
  ['concrete_certifications', CONCRETE_CERT_LABELS],
  ['textile_certifications', TEXTILE_CERT_LABELS],
];

const FILE_FIELDS = [
  ['datasheet',          'Technical datasheet'],
  ['installation_guide', 'Installation guide'],
  ['bim',                'BIM / CAD file'],
  ['hd_download',        'HD swatch / spec pack'],
  ['warranty_doc',       'Warranty certificate'],
  ['catalogue',          'Product catalogue'],
  // Indexed/unverified brands: official product page on the manufacturer's own site.
  ['product_url',        'View on manufacturer site'],
];

// Array-aware: an empty multi-enum array (e.g. `wood_applications: []`)
// should not produce a spec row with a blank value. Scalars work as
// before. `false` booleans ARE considered present — Specifications
// table prints "No" for them, which is meaningful info.
const isPresent = v => {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
};

const formatSpec = (val, kind) => {
  if (kind === 'bool')      return fmtBool(val);
  if (kind === 'pct')       return `${val}%`;
  if (kind === 'mpa')       return `${val} MPa`;
  if (kind === 'mm')        return `${val} mm`;
  // Fabric roll width and areal weight — the two numbers a designer needs to
  // work out yardage and whether a bolt covers a given piece of furniture.
  if (kind === 'cm')        return `${val} cm`;
  if (kind === 'gsm')       return `${val} g/m²`;
  if (kind === 'num')       return String(val);
  if (kind === 'enum')      return humanise(val);
  // Years: support "5" → "5 years", "1" → "1 year".
  if (kind === 'years') {
    const n = Number(val);
    return Number.isFinite(n) ? `${n} ${n === 1 ? 'year' : 'years'}` : String(val);
  }
  // List of slugs (multi-enum) → humanised, comma-separated. Used for
  // wood_applications etc. Defensive against scalar values that may
  // sneak in if the supplier xlsx had a single comma-joined string.
  if (kind === 'enum-list') {
    const arr = Array.isArray(val) ? val : String(val).split(',');
    return arr.map(s => humanise(String(s).trim())).filter(Boolean).join(', ');
  }
  // Ceramic-tile performance: slug list → exact filter-UI labels
  // ("Water resistant (BIa ≤0.5%)"), falling back to humanise for any
  // slug not yet in the map.
  if (kind === 'ctile-perf') {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map(s => CERAMIC_TILE_PERF_LABELS[String(s).trim()] || humanise(String(s).trim()))
      .filter(Boolean)
      .join(', ');
  }
  // Wood finish / surface / pattern / origin / performance: slug list → exact
  // filter-UI labels via the combined WOOD_LABELS map (humanise fallback on a miss).
  if (kind === 'wood-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map(s => WOOD_LABELS[String(s).trim()] || humanise(String(s).trim()))
      .filter(Boolean)
      .join(', ');
  }
  // Laminate & Solid Surfaces facets: slug list → exact filter-UI labels via
  // the combined LAM_SURF_LABELS map (humanise fallback on a miss).
  if (kind === 'lam-surf-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map(s => LAM_SURF_LABELS[String(s).trim()] || humanise(String(s).trim()))
      .filter(Boolean)
      .join(', ');
  }
  if (kind === 'resil-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map(s => RESIL_LABELS[String(s).trim()] || humanise(String(s).trim()))
      .filter(Boolean)
      .join(', ');
  }
  // Carpet & Rug facets: slug list → exact filter-UI labels via CARPET_RUG_LABELS.
  if (kind === 'carpet-rug-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map(s => CARPET_RUG_LABELS[String(s).trim()] || humanise(String(s).trim()))
      .filter(Boolean)
      .join(', ');
  }
  // Glass / metal / concrete / textile filter facets: slug list → exact filter-UI
  // labels via each family's combined map (humanise fallback on a miss). Same shape
  // as wood-list / lam-surf-list — one kind per family keeps the lookup unambiguous.
  if (kind === 'glass-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr.map(s => GLASS_LABELS[String(s).trim()] || humanise(String(s).trim())).filter(Boolean).join(', ');
  }
  if (kind === 'metal-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr.map(s => METAL_LABELS[String(s).trim()] || humanise(String(s).trim())).filter(Boolean).join(', ');
  }
  if (kind === 'concrete-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr.map(s => CONCRETE_LABELS[String(s).trim()] || humanise(String(s).trim())).filter(Boolean).join(', ');
  }
  if (kind === 'textile-list') {
    const arr = Array.isArray(val) ? val : [val];
    return arr.map(s => TEXTILE_LABELS[String(s).trim()] || humanise(String(s).trim())).filter(Boolean).join(', ');
  }
  // Janka hardness in newtons. Render as a thousands-grouped figure with
  // unit suffix ("5,800 N") so designers can scan it without parsing.
  if (kind === 'janka')     return `${Number(val).toLocaleString('en-US')} N`;
  // Areal density / weight per square metre — metal mesh + drapery.
  // Use the proper m² superscript so the spec table reads naturally.
  if (kind === 'kg-m2')     return `${val} kg/m²`;
  return String(val);
};

// Generated FF&E / Architectural facets (specFieldsGenerated.js, auto-built from
// configListing.generated.js by gen_render_rows.py). Each row carries its OWN inline
// labelMap so the ~77 facets never collide on a shared slug. gen-bool → Yes/No;
// gen-num → raw number (the unit lives in the label, e.g. "Acoustic Rw (dB)");
// gen-enum/gen-multi → option label(s), humanise fallback on a miss.
const formatGen = (val, kind, labelMap) => {
  if (kind === 'gen-bool') return fmtBool(val === true || val === 'true' || val === 'yes');
  if (kind === 'gen-num')  return String(val);
  const arr = Array.isArray(val) ? val : [val];
  return arr
    .map(s => (labelMap && labelMap[String(s).trim()]) || humanise(String(s).trim()))
    .filter(Boolean)
    .join(', ');
};

export const SpecsBody = ({ publicData = {} }) => {
  // Hand-maintained materials rows (SPEC_FIELDS) + generated FF&E/Architectural rows.
  // isPresent() gates each listing to only the keys it actually carries, so a chair shows
  // its product facets and a slab shows its stone_* rows from the one shared component.
  const rows = SPEC_FIELDS
    .map(([k, label, kind]) => [k, label, formatSpec(publicData[k], kind), publicData[k]])
    .filter(([, , , v]) => isPresent(v));
  const genRows = GENERATED_SPEC_FIELDS
    .map(([k, label, kind, lm]) => [k, label, formatGen(publicData[k], kind, lm), publicData[k]])
    .filter(([, , , v]) => isPresent(v));
  const all = [...rows, ...genRows];

  if (all.length === 0) {
    return (
      <p className={css.sectionPlaceholder}>
        <FormattedMessage
          id="ListingPage.section.specsEmpty"
          defaultMessage="No specifications provided."
        />
      </p>
    );
  }
  return (
    <dl className={css.specList}>
      {all.map(([k, label, rendered]) => (
        <div key={k} className={css.specRow}>
          <dt className={css.specLabel}>{label}</dt>
          <dd className={css.specValue}>{rendered}</dd>
        </div>
      ))}
    </dl>
  );
};

export const CertificationsBody = ({ publicData = {} }) => {
  const boolRows = CERT_FIELDS.filter(([k]) => publicData[k] === true);
  // Multi-enum cert arrays (e.g. ceramic_tile_certifications) → one ✓ row per
  // slug, keyed `key:slug` to stay unique against the boolean rows.
  const enumRows = [...ENUM_CERT_FIELDS, ...GENERATED_ENUM_CERT_FIELDS].flatMap(([k, labels]) => {
    const arr = publicData[k];
    if (!Array.isArray(arr)) return [];
    return arr.map(slug => [`${k}:${slug}`, labels[slug] || humanise(slug)]);
  });
  // Dedupe by rendered label so a listing that (in some future enrichment)
  // carried both a boolean *_cert_* flag and an overlapping multi-enum slug
  // wouldn't print the same certification twice. Boolean rows win (listed
  // first). Not triggered by current data — belt-and-suspenders.
  const seenLabels = new Set();
  const rows = [...boolRows, ...enumRows].filter(([, label]) => {
    const key = String(label);
    if (seenLabels.has(key)) return false;
    seenLabels.add(key);
    return true;
  });
  // Free-text certification claim as the supplier worded it (written by the bulk
  // uploader from the prep sheet's "Certifications" column). It carries claims with no
  // matching option slug — Greenguard (not Gold), TUV SUD, a certificate number, an
  // EN standard + grade — so it is shown under the ✓ list, labelled as the supplier's
  // own statement rather than as a verified ✓ item. Skipped when it only repeats a ✓ label.
  const rawStatement =
    typeof publicData.certifications_text === 'string' ? publicData.certifications_text.trim() : '';
  const statement =
    rawStatement && !seenLabels.has(rawStatement) ? rawStatement : '';

  if (rows.length === 0 && !statement) {
    return (
      <p className={css.sectionPlaceholder}>
        <FormattedMessage
          id="ListingPage.section.certsEmpty"
          defaultMessage="No certifications listed."
        />
      </p>
    );
  }
  return (
    <>
      {rows.length > 0 ? (
        <ul className={css.certList}>
          {rows.map(([k, label]) => (
            <li key={k} className={css.certItem}>
              <span className={css.certCheck} aria-hidden="true">✓</span>
              {label}
            </li>
          ))}
        </ul>
      ) : null}
      {statement ? (
        <p className={rows.length > 0 ? css.certStatement : css.certStatementOnly}>
          <span className={css.certStatementLabel}>
            <FormattedMessage
              id="ListingPage.section.certsSupplierStatement"
              defaultMessage="Supplier statement"
            />
          </span>
          {statement}
        </p>
      ) : null}
    </>
  );
};

// Supplier-attested property claims (not third-party certified).
// Rendered with the same visual treatment as certs (bullet + ✓) but
// in a distinct collapsible dropdown so designers can tell verified
// standards apart from manufacturer marketing copy. SUZUKA was the
// trigger — STRATO products advertise nine features ("Green Product",
// "Low VOC", "Applicable on Ceiling", "Easy Application", etc.) that
// shouldn't be conflated with LEED / California Prop 65 / SIRIM.
export const FeaturesBody = ({ publicData = {} }) => {
  const rows = FEATURE_FIELDS.filter(([k]) => publicData[k] === true);
  if (rows.length === 0) {
    return (
      <p className={css.sectionPlaceholder}>
        <FormattedMessage
          id="ListingPage.section.featuresEmpty"
          defaultMessage="No features listed."
        />
      </p>
    );
  }
  return (
    <ul className={css.certList}>
      {rows.map(([k, label]) => (
        <li key={k} className={css.certItem}>
          <span className={css.certCheck} aria-hidden="true">✓</span>
          {label}
        </li>
      ))}
    </ul>
  );
};

export const FilesBody = ({ publicData = {} }) => {
  const rows = FILE_FIELDS
    .map(([k, label]) => [k, label, publicData[k]])
    .filter(([, , v]) => typeof v === 'string' && v.trim() !== '');

  if (rows.length === 0) {
    return (
      <p className={css.sectionPlaceholder}>
        <FormattedMessage
          id="ListingPage.section.filesEmpty"
          defaultMessage="No files attached."
        />
      </p>
    );
  }
  return (
    <ul className={css.filesList}>
      {rows.map(([k, label, url]) => (
        <li key={k} className={css.filesItem}>
          <a href={url} target="_blank" rel="noopener noreferrer" className={css.filesLink}>
            {label}
            <span className={css.filesArrow} aria-hidden="true">↗</span>
          </a>
        </li>
      ))}
    </ul>
  );
};

// "What's included & warranty" — generic, brand-agnostic keys written at
// enrichment time: publicData.whats_included (newline-separated box-contents
// lines, e.g. "1 x pair of sprung door handles\n2 x M4x35mm through bolts")
// and publicData.warranty (supplier warranty terms, plain text; \n\n =
// paragraph break). Unlike Specifications/Files/Certifications this section
// HIDES entirely when neither key is present — gate the CollapsibleSection at
// the call site with hasIncludedWarranty(publicData).
const cleanStr = v => (typeof v === 'string' ? v.trim() : '');

export const hasIncludedWarranty = (publicData = {}) =>
  cleanStr(publicData.whats_included) !== '' || cleanStr(publicData.warranty) !== '';

export const IncludedWarrantyBody = ({ publicData = {} }) => {
  const included = cleanStr(publicData.whats_included);
  const warranty = cleanStr(publicData.warranty);
  const includedLines = included
    .split('\n')
    .map(l => l.trim())
    .filter(l => l !== '');
  return (
    <>
      {includedLines.length > 0 ? (
        <>
          <p className={css.inclSubheading}>
            <FormattedMessage
              id="ListingPage.section.includedWarrantyIncluded"
              defaultMessage="What's included"
            />
          </p>
          <ul className={css.inclList}>
            {includedLines.map((line, i) => (
              <li key={`incl-${i}`} className={css.inclItem}>
                {line}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {warranty !== '' ? (
        <>
          <p className={css.inclSubheading}>
            <FormattedMessage
              id="ListingPage.section.includedWarrantyWarranty"
              defaultMessage="Warranty"
            />
          </p>
          <p className={css.warrantyText}>{warranty}</p>
        </>
      ) : null}
    </>
  );
};
